import { useQuery } from '@tanstack/react-query'
import { useMemo, useRef, useState } from 'react'
import { getTimeseries, type TimeseriesPeriod, type TimeseriesResponse, type TimeseriesPoint } from '@/api/timeseries'

interface Props {
  channelUUID: string
  period?: TimeseriesPeriod
}

const W = 720
const H = 280
const PAD_LEFT = 50
const PAD_RIGHT = 0
const PAD_TOP = 30
const PAD_BOTTOM = 36

interface PixelPoint {
  i: number
  px: number
  py: number
  value: number
  date: string
}

export function SubscribersChart({ channelUUID, period = 'month' }: Props) {
  const { data, isLoading } = useQuery({
    queryKey: ['timeseries', channelUUID, 'subscribers', period],
    queryFn: () => getTimeseries(channelUUID, 'subscribers', period),
    enabled: !!channelUUID,
    staleTime: 5 * 60_000,
  })

  const chart = useMemo(() => buildChart(data), [data])

  const svgRef = useRef<SVGSVGElement>(null)
  const [hover, setHover] = useState<{
    current: PixelPoint
    previous: PixelPoint | null
    clientX: number
    clientY: number
  } | null>(null)

  const handleMouseMove = (e: React.MouseEvent<SVGSVGElement>) => {
    if (!svgRef.current || chart.currentPoints.length === 0) return
    const rect = svgRef.current.getBoundingClientRect()
    // переводим mouse X в координаты viewBox
    const xVB = ((e.clientX - rect.left) / rect.width) * W
    // ищем ближайшую точку
    let nearest = chart.currentPoints[0]
    let minDist = Math.abs(nearest.px - xVB)
    for (const p of chart.currentPoints) {
      const d = Math.abs(p.px - xVB)
      if (d < minDist) {
        minDist = d
        nearest = p
      }
    }
    const prev = chart.previousPoints[nearest.i] || null
    setHover({ current: nearest, previous: prev, clientX: e.clientX - rect.left, clientY: e.clientY - rect.top })
  }

  const handleMouseLeave = () => setHover(null)

  return (
    <div className="card chart-card">
      <div className="card-head">
        <div>
          <h3 className="card-title">
            Прирост подписчиков, <em>динамика</em>
          </h3>
          <div className="card-sub">{chart.subtitle}</div>
        </div>
      </div>
      <div className="card-meta">
        <span className="card-meta-pill">
          <span className="dot" /> Текущий период
        </span>
        <span className="card-meta-pill">
          <span className="dot alt" /> Предыдущий период
        </span>
      </div>

      <div className="chart-wrap">
        {isLoading ? (
          <div className="chart-loading">Загрузка графика…</div>
        ) : (
          <>
            <svg
              ref={svgRef}
              viewBox={`0 0 ${W} ${H}`}
              preserveAspectRatio="none"
              className="chart-svg"
              onMouseMove={handleMouseMove}
              onMouseLeave={handleMouseLeave}
            >
              <defs>
                <linearGradient id="line-grad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0" stopColor="#0080FF" stopOpacity="0.18" />
                  <stop offset="1" stopColor="#0080FF" stopOpacity="0" />
                </linearGradient>
              </defs>

              {/* gridlines */}
              {chart.yTicks.map((tick) => (
                <g key={tick.y}>
                  <line x1={PAD_LEFT} y1={tick.y} x2={W} y2={tick.y} stroke="rgba(143,158,176,.16)" />
                  <text
                    x={PAD_LEFT - 6}
                    y={tick.y + 4}
                    textAnchor="end"
                    fontFamily="Inter"
                    fontSize="10"
                    fontWeight="500"
                    fill="#8F9EB0"
                  >
                    {tick.label}
                  </text>
                </g>
              ))}

              {/* x labels */}
              {chart.xTicks.map((tick) => (
                <text
                  key={tick.x}
                  x={tick.x}
                  y={H - 8}
                  fontFamily="Inter"
                  fontSize="10"
                  fontWeight="500"
                  fill="#8F9EB0"
                >
                  {tick.label}
                </text>
              ))}

              {/* previous line */}
              {chart.previousPath && (
                <path
                  d={chart.previousPath}
                  fill="none"
                  stroke="#C7CDD3"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeDasharray="4 4"
                />
              )}

              {/* current area + line */}
              {chart.currentArea && <path d={chart.currentArea} fill="url(#line-grad)" />}
              {chart.currentPath && (
                <path
                  d={chart.currentPath}
                  fill="none"
                  stroke="#0080FF"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              )}

              {/* hover indicator */}
              {hover && (
                <g>
                  <line
                    x1={hover.current.px}
                    x2={hover.current.px}
                    y1={PAD_TOP}
                    y2={H - PAD_BOTTOM}
                    stroke="#0080FF"
                    strokeWidth="1"
                    strokeDasharray="2 3"
                    opacity="0.5"
                  />
                  {hover.previous && (
                    <circle cx={hover.previous.px} cy={hover.previous.py} r="4" fill="#fff" stroke="#C7CDD3" strokeWidth="2" />
                  )}
                  <circle cx={hover.current.px} cy={hover.current.py} r="5" fill="#fff" stroke="#0080FF" strokeWidth="2.5" />
                </g>
              )}
            </svg>

            {hover && (
              <div
                className="chart-tooltip"
                style={{
                  left: tooltipLeft(hover.clientX),
                  top: hover.clientY - 12,
                }}
              >
                <div className="tt-date">{formatDateLabel(hover.current.date)}</div>
                <div className="tt-row">
                  <span className="tt-dot" />
                  <span className="tt-label">Текущий</span>
                  <span className="tt-val">{formatNum(hover.current.value)}</span>
                </div>
                {hover.previous && (
                  <div className="tt-row">
                    <span className="tt-dot alt" />
                    <span className="tt-label">Предыдущий</span>
                    <span className="tt-val">{formatNum(hover.previous.value)}</span>
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </div>

      <style>{`
        .chart-card {
          background: var(--tg-surface, #fff);
          border: 1px solid var(--tg-line-2, #f0f0f4);
          border-radius: 16px;
          padding: 22px 24px;
          position: relative;
        }
        .card-head {
          display: flex;
          justify-content: space-between;
          align-items: flex-start;
          margin-bottom: 12px;
        }
        .card-title {
          margin: 0;
          font-size: 16px;
          font-weight: 700;
          color: var(--tg-ink, #111);
        }
        .card-title em {
          font-style: italic;
          font-family: 'Instrument Serif', serif;
          font-weight: 400;
          color: var(--tg-mute, #8f9eb0);
        }
        .card-sub {
          font-size: 12px;
          color: var(--tg-mute, #8f9eb0);
          margin-top: 4px;
        }
        .card-meta {
          display: flex;
          gap: 14px;
          margin-bottom: 16px;
        }
        .card-meta-pill {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          font-size: 11px;
          font-weight: 600;
          color: var(--tg-mute, #8f9eb0);
        }
        .card-meta-pill .dot {
          width: 8px;
          height: 8px;
          border-radius: 50%;
          background: #0080FF;
        }
        .card-meta-pill .dot.alt {
          background: #C7CDD3;
        }
        .chart-wrap {
          width: 100%;
          aspect-ratio: 720 / 280;
          position: relative;
        }
        .chart-svg {
          width: 100%;
          height: 100%;
          cursor: crosshair;
        }
        .chart-loading {
          display: flex;
          align-items: center;
          justify-content: center;
          height: 100%;
          color: var(--tg-mute, #888);
          font-size: 13px;
        }
        .chart-tooltip {
          position: absolute;
          transform: translate(-50%, -100%);
          background: var(--tg-ink, #0a0a0f);
          color: white;
          padding: 8px 12px;
          border-radius: 8px;
          font-size: 12px;
          pointer-events: none;
          box-shadow: 0 4px 16px rgba(0,0,0,0.16);
          white-space: nowrap;
          z-index: 5;
        }
        .tt-date {
          font-weight: 700;
          font-size: 11px;
          color: rgba(255,255,255,0.7);
          margin-bottom: 6px;
          text-transform: uppercase;
          letter-spacing: 0.04em;
        }
        .tt-row {
          display: flex;
          align-items: center;
          gap: 6px;
          font-variant-numeric: tabular-nums;
        }
        .tt-row + .tt-row {
          margin-top: 4px;
        }
        .tt-dot {
          width: 6px;
          height: 6px;
          border-radius: 50%;
          background: #0080FF;
        }
        .tt-dot.alt {
          background: #C7CDD3;
        }
        .tt-label {
          color: rgba(255,255,255,0.6);
          margin-right: 6px;
        }
        .tt-val {
          font-weight: 700;
          margin-left: auto;
        }
      `}</style>
    </div>
  )
}

function tooltipLeft(x: number): number {
  return x
}

function formatNum(n: number): string {
  return n.toLocaleString('ru-RU')
}

interface ChartData {
  subtitle: string
  yTicks: Array<{ y: number; label: string }>
  xTicks: Array<{ x: number; label: string }>
  currentPath: string | null
  currentArea: string | null
  previousPath: string | null
  currentPoints: PixelPoint[]
  previousPoints: PixelPoint[]
}

function buildChart(data: TimeseriesResponse | undefined): ChartData {
  const empty: ChartData = {
    subtitle: '',
    yTicks: [],
    xTicks: [],
    currentPath: null,
    currentArea: null,
    previousPath: null,
    currentPoints: [],
    previousPoints: [],
  }
  if (!data) return empty

  const all = [...data.current.points, ...data.previous.points]
  if (all.length === 0) return { ...empty, subtitle: 'Нет данных' }

  const minY = Math.min(...all.map((p) => p.value))
  const maxY = Math.max(...all.map((p) => p.value))
  const padding = (maxY - minY) * 0.15 || maxY * 0.15 || 10
  const yMin = Math.max(0, Math.floor(minY - padding))
  const yMax = Math.ceil(maxY + padding)

  const chartW = W - PAD_LEFT - PAD_RIGHT
  const chartH = H - PAD_TOP - PAD_BOTTOM
  const yToPx = (v: number) => PAD_TOP + chartH * (1 - (v - yMin) / (yMax - yMin || 1))

  const totalPoints = data.current.points.length
  const xToPx = (i: number) => PAD_LEFT + (chartW * i) / Math.max(1, totalPoints - 1)

  const toPixels = (points: TimeseriesPoint[]): PixelPoint[] =>
    points.map((p, i) => ({ i, px: xToPx(i), py: yToPx(p.value), value: p.value, date: p.date }))

  const currentPoints = toPixels(data.current.points)
  const previousPoints = toPixels(data.previous.points)

  const buildPath = (pixels: PixelPoint[]): string => {
    if (pixels.length === 0) return ''
    return pixels.map((p, i) => `${i === 0 ? 'M' : 'L'}${p.px.toFixed(1)},${p.py.toFixed(1)}`).join(' ')
  }

  const buildArea = (pixels: PixelPoint[]): string => {
    if (pixels.length === 0) return ''
    const top = pixels.map((p, i) => `${i === 0 ? 'M' : 'L'}${p.px.toFixed(1)},${p.py.toFixed(1)}`).join(' ')
    const lastX = pixels[pixels.length - 1].px.toFixed(1)
    const firstX = pixels[0].px.toFixed(1)
    const bottomY = (PAD_TOP + chartH).toFixed(1)
    return `${top} L${lastX},${bottomY} L${firstX},${bottomY} Z`
  }

  const yTicks = [0, 1, 2, 3].map((i) => {
    const value = yMin + ((yMax - yMin) * (3 - i)) / 3
    return {
      y: PAD_TOP + (chartH * i) / 3,
      label: Math.round(value).toLocaleString('ru-RU'),
    }
  })

  const xTicks =
    totalPoints > 0
      ? [0, Math.floor(totalPoints / 3), Math.floor((2 * totalPoints) / 3), totalPoints - 1].map((i) => ({
          x: xToPx(i),
          label: formatDateLabel(data.current.points[i]?.date || ''),
        }))
      : []

  const fromDate = formatDateLabel(data.current.from.slice(0, 10))
  const toDate = formatDateLabel(data.current.to.slice(0, 10))

  return {
    subtitle: `с ${fromDate} по ${toDate} · сравнение с предыдущим периодом`,
    yTicks,
    xTicks,
    currentPath: buildPath(currentPoints),
    currentArea: buildArea(currentPoints),
    previousPath: buildPath(previousPoints),
    currentPoints,
    previousPoints,
  }
}

function formatDateLabel(iso: string): string {
  if (!iso) return ''
  const [, m, d] = iso.split('-')
  const months = ['янв', 'фев', 'мар', 'апр', 'май', 'июн', 'июл', 'авг', 'сен', 'окт', 'ноя', 'дек']
  return `${parseInt(d, 10)} ${months[parseInt(m, 10) - 1]}`
}
